import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguageContext } from '../contexts/LanguageContext';

interface NewsItem {
  _id: string;
  slug: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  publishedAt: string;
}

interface NewsCompactGridProps {
  className?: string;
}

const NewsCompactGrid: React.FC<NewsCompactGridProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { currentLanguage: language } = useLanguageContext();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestNews();
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLatestNews = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/news/latest?limit=3&language=${language}`);
      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }
      const data = await response.json();
      setNews(data.news || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };



  if (isLoading) {
    return (
      <div className={`bg-black text-white py-16 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-48 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-64 mx-auto"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="h-48 bg-gray-700"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-700 rounded w-1/4 mb-3"></div>
                    <div className="h-6 bg-gray-700 rounded mb-3"></div>
                    <div className="h-4 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return null; // Don't show section if no news
  }

  return (
    <div className={`bg-black text-white py-16 ${className}`}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">
            {t('news.latest_news')}
          </h2>
          <p className="text-gray-400 text-lg">
            {t('news.stay_updated')}
          </p>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {news.map((item) => (
            <article 
              key={item._id} 
              className="bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors duration-200 group flex flex-col h-full"
            >
              <div className="h-48 overflow-hidden">
                <Link to={`/news/${item.slug}`} className="block h-full">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/new_logo.png'; // Fallback image
                    }}
                  />
                </Link>
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <div className="text-sm text-purple-400 mb-3">
                  {formatDate(item.publishedAt)}
                </div>
                
                <h3 className="text-xl font-bold mb-3 text-white group-hover:text-purple-400 transition-colors duration-200 min-h-[3.5rem] line-clamp-2">
                  <Link to={`/news/${item.slug}`}>
                    {item.title.replace(/\\"/g, '"')}
                  </Link>
                </h3>
                
                <p className="text-gray-300 mb-4 leading-relaxed flex-grow line-clamp-3">
                  {item.subtitle.replace(/\\"/g, '"')}
                </p>
                
                <div className="mt-auto">
                  <Link
                    to={`/news/${item.slug}`}
                    className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-200"
                  >
                    {t('news.read_more')}
                    <svg 
                      className="ml-2 w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All News Button */}
        <div className="text-center">
          <Link
            to="/news"
            className="inline-flex items-center border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-3 rounded-lg transition-colors duration-200"
          >
            {t('news.see_all_news')}
            <svg 
              className="ml-2 w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NewsCompactGrid; 